import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { 
  Shield, Users, MessageSquare, AlertTriangle, Eye, EyeOff, 
  Trash2, ChevronRight, Loader2, RefreshCw, BarChart3, Database, CheckCircle2, XCircle, Sparkles,
  Play, Pause, Clock, TrendingUp, Bot, UserCheck
} from "lucide-react";
import { api, parseApiError, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, status } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [togglingAi, setTogglingAi] = useState(false);
  
  // Seeding state
  const [seeding, setSeeding] = useState(false);
  const [seedStatus, setSeedStatus] = useState(null);
  const [seedMessage, setSeedMessage] = useState("");
  
  // Scheduler state
  const [schedulerConfig, setSchedulerConfig] = useState(null);
  const [schedulerLoading, setSchedulerLoading] = useState(false);
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);

  const isAdmin = user?.email === "ktsonline24@gmail.com";

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, reportsRes, schedulerRes, analyticsRes] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/reports?limit=10"),
        api.get("/admin/scheduler/config").catch(() => ({ data: null })),
        api.get("/admin/analytics/users").catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setReports(reportsRes.data.items || []);
      setAiEnabled(statsRes.data.ai_bootstrap_enabled);
      if (schedulerRes.data) setSchedulerConfig(schedulerRes.data);
      if (analyticsRes.data) setAnalytics(analyticsRes.data);
    } catch (e) {
      console.error("Admin data fetch failed:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (status === "authed" && isAdmin) {
      fetchData();
    } else if (status === "authed" && !isAdmin) {
      navigate("/");
    }
  }, [status, isAdmin, fetchData, navigate]);

  const toggleAiBootstrap = async () => {
    setTogglingAi(true);
    try {
      await api.post("/admin/ai-bootstrap/toggle", { enabled: !aiEnabled });
      setAiEnabled(!aiEnabled);
    } catch (e) {
      alert(parseApiError(e, "Toggle failed"));
    } finally {
      setTogglingAi(false);
    }
  };

  const hideContent = async (subjectType, subjectId) => {
    try {
      await api.post(`/admin/hide/${subjectType}/${subjectId}`);
      fetchData();
    } catch (e) {
      alert(parseApiError(e, "Hide failed"));
    }
  };

  const unhideContent = async (subjectType, subjectId) => {
    try {
      await api.post(`/admin/unhide/${subjectType}/${subjectId}`);
      fetchData();
    } catch (e) {
      alert(parseApiError(e, "Unhide failed"));
    }
  };

  const dismissReport = async (reportId) => {
    try {
      await api.post(`/admin/reports/${reportId}/dismiss`);
      fetchData();
    } catch (e) {
      alert(parseApiError(e, "Dismiss failed"));
    }
  };

  // Scheduler controls
  const toggleScheduler = async () => {
    setSchedulerLoading(true);
    try {
      const endpoint = schedulerConfig?.enabled ? "/admin/scheduler/pause" : "/admin/scheduler/resume";
      await api.post(endpoint);
      setSchedulerConfig(prev => ({ ...prev, enabled: !prev?.enabled }));
    } catch (e) {
      alert(parseApiError(e, "Toggle failed"));
    } finally {
      setSchedulerLoading(false);
    }
  };

  const updateSchedulerConfig = async (updates) => {
    setSchedulerLoading(true);
    try {
      const res = await api.post("/admin/scheduler/config", updates);
      setSchedulerConfig(res.data.config);
    } catch (e) {
      alert(parseApiError(e, "Update failed"));
    } finally {
      setSchedulerLoading(false);
    }
  };

  const seedDatabase = async () => {
    if (!window.confirm(
      "This will populate the database with 43 Bangalore-centric questions with AI-generated images and answers.\n\n" +
      "This process takes about 10-15 minutes and uses AI credits.\n\n" +
      "Continue?"
    )) {
      return;
    }
    
    setSeeding(true);
    setSeedStatus(null);
    setSeedMessage("Starting database seeding... This will take 10-15 minutes.");
    
    try {
      const response = await api.post("/admin/seed-bangalore");
      setSeedStatus("success");
      setSeedMessage(response.data.message || "Database seeding started successfully!");
      setTimeout(() => fetchData(), 5000);
    } catch (e) {
      setSeedStatus("error");
      setSeedMessage(parseApiError(e, "Failed to start seeding."));
    } finally {
      setSeeding(false);
    }
  };

  if (status === "loading" || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-900">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center px-4">
          <Shield className="mx-auto h-16 w-16 text-slate-600" />
          <h1 className="mt-4 text-2xl font-bold text-slate-100">Access Denied</h1>
          <p className="mt-2 text-slate-400">You don&apos;t have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950">
      <div className="mx-auto max-w-6xl px-4 py-8">
        <Seo title="Admin Dashboard" />

        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-100">
              <Shield className="h-6 w-6 text-indigo-400" />
              Admin Dashboard
            </h1>
            <p className="mt-1 text-sm text-slate-500">Manage content and monitor platform activity</p>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchData} 
            className="rounded-full border-slate-700 bg-slate-800/50 text-slate-300 hover:bg-slate-800 hover:text-slate-100"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="border-blue-500/20 bg-blue-500/10 backdrop-blur-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-blue-400">{stats?.total_questions || 0}</div>
                <div className="text-sm text-slate-400">Total Questions</div>
              </div>
              <MessageSquare className="h-8 w-8 text-blue-500/50" />
            </div>
          </Card>

          <Card className="border-emerald-500/20 bg-emerald-500/10 backdrop-blur-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-emerald-400">{stats?.total_answers || 0}</div>
                <div className="text-sm text-slate-400">Total Answers</div>
                <div className="mt-1 text-xs text-slate-500">
                  AI: {stats?.ai_answers || 0} | Human: {stats?.human_answers || 0}
                </div>
              </div>
              <BarChart3 className="h-8 w-8 text-emerald-500/50" />
            </div>
          </Card>

          <Card className="border-violet-500/20 bg-violet-500/10 backdrop-blur-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-violet-400">{stats?.total_users || 0}</div>
                <div className="text-sm text-slate-400">Registered Users</div>
              </div>
              <Users className="h-8 w-8 text-violet-500/50" />
            </div>
          </Card>

          <Card className="border-amber-500/20 bg-amber-500/10 backdrop-blur-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-amber-400">{stats?.pending_reports || 0}</div>
                <div className="text-sm text-slate-400">Pending Reports</div>
                <div className="mt-1 text-xs text-slate-500">
                  Hidden: {stats?.hidden_questions || 0}Q / {stats?.hidden_answers || 0}A
                </div>
              </div>
              <AlertTriangle className="h-8 w-8 text-amber-500/50" />
            </div>
          </Card>
        </div>

        {/* AI Bootstrap Toggle */}
        <Card className="mt-6 border-slate-700/50 bg-slate-800/50 backdrop-blur-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-100">AI Answer Bootstrap</h3>
              <p className="text-sm text-slate-400">
                When enabled, AI automatically generates initial responses and community-style answers for new questions.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium ${aiEnabled ? "text-emerald-400" : "text-slate-500"}`}>
                {aiEnabled ? "Enabled" : "Disabled"}
              </span>
              <Switch
                checked={aiEnabled}
                onCheckedChange={toggleAiBootstrap}
                disabled={togglingAi}
              />
            </div>
          </div>
        </Card>

        {/* DATABASE SEEDING */}
        <Card className="mt-6 border-indigo-500/20 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 backdrop-blur-sm p-6">
          <div className="flex items-start gap-4">
            <div className="rounded-full bg-indigo-500/20 p-3">
              <Database className="h-6 w-6 text-indigo-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-100">Populate Database</h3>
              <p className="mt-1 text-sm text-slate-400">
                Click the button below to add Bangalore-centric questions with AI-generated images and answers.
              </p>
              
              {seedMessage && (
                <div className={`mt-3 flex items-center gap-2 rounded-lg p-3 ${
                  seedStatus === "success" ? "bg-emerald-500/20 text-emerald-300" :
                  seedStatus === "error" ? "bg-red-500/20 text-red-300" :
                  "bg-indigo-500/20 text-indigo-300"
                }`}>
                  {seedStatus === "success" && <CheckCircle2 className="h-5 w-5" />}
                  {seedStatus === "error" && <XCircle className="h-5 w-5" />}
                  {!seedStatus && <Loader2 className="h-5 w-5 animate-spin" />}
                  <span className="text-sm">{seedMessage}</span>
                </div>
              )}
              
              <div className="mt-4 flex flex-wrap gap-3">
                <Button
                  onClick={seedDatabase}
                  disabled={seeding}
                  className="rounded-lg bg-indigo-600 px-6 text-white hover:bg-indigo-500 disabled:opacity-50"
                  data-testid="seed-database-btn"
                >
                  {seeding ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Seeding...
                    </>
                  ) : (
                    <>
                      <Database className="mr-2 h-4 w-4" />
                      Seed Bangalore Questions
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={async () => {
                    if (!window.confirm("Post 10 new Hinglish questions with AI answers?")) return;
                    setSeedMessage("Posting Hinglish content...");
                    try {
                      await api.post("/admin/seed-hinglish-content?count=10");
                      setSeedStatus("success");
                      setSeedMessage("10 Hinglish questions are being posted!");
                      setTimeout(fetchData, 10000);
                    } catch (e) {
                      setSeedStatus("error");
                      setSeedMessage(parseApiError(e, "Failed to post content"));
                    }
                  }}
                  disabled={seeding}
                  className="rounded-lg bg-purple-600 px-6 text-white hover:bg-purple-500 disabled:opacity-50"
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  Post Hinglish Content
                </Button>
                
                {/* Quick Seed - No AI required */}
                <Button
                  onClick={async () => {
                    if (!window.confirm("Quick seed 50 questions with stock images? (Instant, no AI needed)")) return;
                    setSeeding(true);
                    setSeedMessage("Quick seeding 50 questions with stock images...");
                    setSeedStatus(null);
                    try {
                      const res = await api.post("/admin/seed-quick?count=50");
                      setSeedStatus("success");
                      setSeedMessage(res.data.message || `Added ${res.data.added} questions with images!`);
                      setTimeout(fetchData, 2000);
                    } catch (e) {
                      setSeedStatus("error");
                      // Show detailed error
                      const errDetail = e.response?.data?.detail || e.message || "Unknown error";
                      setSeedMessage(`Quick seed failed: ${errDetail}`);
                      console.error("Quick seed error:", e.response?.data || e);
                    } finally {
                      setSeeding(false);
                    }
                  }}
                  disabled={seeding}
                  className="rounded-lg bg-emerald-600 px-6 text-white hover:bg-emerald-500 disabled:opacity-50"
                  data-testid="quick-seed-btn"
                >
                  <Database className="mr-2 h-4 w-4" />
                  Quick Seed + Images
                </Button>
                
                {/* Seed with Translations */}
                <Button
                  onClick={async () => {
                    if (!window.confirm("Seed 10 questions with translations in 6 languages? (English, Hindi, Kannada, Tamil, Telugu, Malayalam)")) return;
                    setSeeding(true);
                    setSeedMessage("Seeding questions with translations...");
                    setSeedStatus(null);
                    try {
                      const res = await api.post("/admin/seed-translated?count=10");
                      setSeedStatus("success");
                      setSeedMessage(res.data.message || `Added ${res.data.added} translated questions!`);
                      setTimeout(fetchData, 2000);
                    } catch (e) {
                      setSeedStatus("error");
                      const errDetail = e.response?.data?.detail || e.message || "Unknown error";
                      setSeedMessage(`Translated seed failed: ${errDetail}`);
                    } finally {
                      setSeeding(false);
                    }
                  }}
                  disabled={seeding}
                  className="rounded-lg bg-cyan-600 px-6 text-white hover:bg-cyan-500 disabled:opacity-50"
                  data-testid="seed-translated-btn"
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  Seed with Translations
                </Button>
                
                {/* Fix Broken Images - Replace local URLs with stock images */}
                <Button
                  onClick={async () => {
                    if (!window.confirm("This will replace all broken local image URLs with working stock images from Unsplash. Continue?")) return;
                    setSeeding(true);
                    setSeedMessage("Fixing broken images...");
                    setSeedStatus(null);
                    try {
                      const res = await api.post("/admin/fix-broken-images");
                      setSeedStatus("success");
                      setSeedMessage(res.data.message || `Fixed ${res.data.fixed} broken images!`);
                      setTimeout(fetchData, 2000);
                    } catch (e) {
                      setSeedStatus("error");
                      const errDetail = e.response?.data?.detail || e.message || "Unknown error";
                      setSeedMessage(`Fix images failed: ${errDetail}`);
                    } finally {
                      setSeeding(false);
                    }
                  }}
                  disabled={seeding}
                  className="rounded-lg bg-orange-600 px-6 text-white hover:bg-orange-500 disabled:opacity-50"
                  data-testid="fix-images-btn"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Fix Broken Images
                </Button>
              </div>
                
              <p className="mt-2 text-xs text-slate-500">
                <span className="text-emerald-400">Quick Seed = Instant with stock images</span> • Others take 10-15 min & use AI credits
              </p>
            </div>
          </div>
        </Card>

        {/* CONTENT SCHEDULER CONTROLS */}
        <Card className="mt-6 border-cyan-500/20 bg-gradient-to-r from-cyan-500/10 to-teal-500/10 backdrop-blur-sm p-6">
          <div className="flex items-start gap-4">
            <div className="rounded-full bg-cyan-500/20 p-3">
              <Clock className="h-6 w-6 text-cyan-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-100">Content Scheduler</h3>
              <p className="mt-1 text-sm text-slate-400">
                Automated AI content posting. Control frequency, daily limits, and pause/resume.
              </p>
              
              {schedulerConfig && (
                <div className="mt-4 space-y-4">
                  {/* Status & Toggle */}
                  <div className="flex items-center justify-between rounded-lg bg-slate-800/50 p-3">
                    <div className="flex items-center gap-3">
                      {schedulerConfig.enabled ? (
                        <Play className="h-5 w-5 text-emerald-400" />
                      ) : (
                        <Pause className="h-5 w-5 text-amber-400" />
                      )}
                      <span className={`font-medium ${schedulerConfig.enabled ? "text-emerald-400" : "text-amber-400"}`}>
                        {schedulerConfig.enabled ? "Running" : "Paused"}
                      </span>
                      <span className="text-sm text-slate-500">
                        • Posted {schedulerConfig.today_count || 0} today
                      </span>
                    </div>
                    <Button
                      onClick={toggleScheduler}
                      disabled={schedulerLoading}
                      variant="outline"
                      size="sm"
                      className={`rounded-full ${
                        schedulerConfig.enabled 
                          ? "border-amber-500 text-amber-400 hover:bg-amber-500/20" 
                          : "border-emerald-500 text-emerald-400 hover:bg-emerald-500/20"
                      }`}
                    >
                      {schedulerLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : schedulerConfig.enabled ? (
                        <>
                          <Pause className="mr-1 h-4 w-4" />
                          Pause
                        </>
                      ) : (
                        <>
                          <Play className="mr-1 h-4 w-4" />
                          Resume
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Settings Grid */}
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-lg bg-slate-800/30 p-3">
                      <label className="text-xs text-slate-500">Post Interval</label>
                      <select
                        value={schedulerConfig.interval_minutes || 30}
                        onChange={(e) => updateSchedulerConfig({ interval_minutes: parseInt(e.target.value) })}
                        className="mt-1 w-full rounded bg-slate-700 px-2 py-1.5 text-sm text-slate-200"
                      >
                        <option value={15}>Every 15 mins</option>
                        <option value={30}>Every 30 mins</option>
                        <option value={60}>Every 1 hour</option>
                        <option value={120}>Every 2 hours</option>
                      </select>
                    </div>
                    
                    <div className="rounded-lg bg-slate-800/30 p-3">
                      <label className="text-xs text-slate-500">Daily Limit</label>
                      <select
                        value={schedulerConfig.daily_limit || 48}
                        onChange={(e) => updateSchedulerConfig({ daily_limit: parseInt(e.target.value) })}
                        className="mt-1 w-full rounded bg-slate-700 px-2 py-1.5 text-sm text-slate-200"
                      >
                        <option value={24}>24 posts/day</option>
                        <option value={48}>48 posts/day</option>
                        <option value={72}>72 posts/day</option>
                        <option value={96}>96 posts/day</option>
                      </select>
                    </div>
                    
                    <div className="rounded-lg bg-slate-800/30 p-3">
                      <label className="text-xs text-slate-500">Answers/Question</label>
                      <select
                        value={schedulerConfig.answers_per_question || 3}
                        onChange={(e) => updateSchedulerConfig({ answers_per_question: parseInt(e.target.value) })}
                        className="mt-1 w-full rounded bg-slate-700 px-2 py-1.5 text-sm text-slate-200"
                      >
                        <option value={2}>2 answers</option>
                        <option value={3}>3 answers</option>
                        <option value={4}>4 answers</option>
                        <option value={5}>5 answers</option>
                      </select>
                    </div>
                    
                    <div className="rounded-lg bg-slate-800/30 p-3">
                      <label className="text-xs text-slate-500">Fun Questions</label>
                      <select
                        value={Math.round((schedulerConfig.fun_question_ratio || 0.2) * 100)}
                        onChange={(e) => updateSchedulerConfig({ fun_question_ratio: parseInt(e.target.value) / 100 })}
                        className="mt-1 w-full rounded bg-slate-700 px-2 py-1.5 text-sm text-slate-200"
                      >
                        <option value={10}>10% fun</option>
                        <option value={20}>20% fun (1 in 5)</option>
                        <option value={30}>30% fun</option>
                        <option value={50}>50% fun</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* USER ANALYTICS */}
        <Card className="mt-6 border-purple-500/20 bg-gradient-to-r from-purple-500/10 to-pink-500/10 backdrop-blur-sm p-6">
          <div className="flex items-start gap-4">
            <div className="rounded-full bg-purple-500/20 p-3">
              <TrendingUp className="h-6 w-6 text-purple-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-100">User Analytics</h3>
              <p className="mt-1 text-sm text-slate-400">
                Real vs AI content breakdown and user engagement metrics.
              </p>
              
              {analytics && (
                <div className="mt-4 space-y-4">
                  {/* Content Breakdown */}
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-lg bg-slate-800/30 p-3">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Bot className="h-4 w-4" />
                        AI Questions
                      </div>
                      <div className="mt-1 text-xl font-bold text-amber-400">
                        {analytics.content?.seeded_questions || 0}
                      </div>
                    </div>
                    
                    <div className="rounded-lg bg-slate-800/30 p-3">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <UserCheck className="h-4 w-4" />
                        Real Questions
                      </div>
                      <div className="mt-1 text-xl font-bold text-emerald-400">
                        {analytics.content?.real_questions || 0}
                        <span className="ml-2 text-sm font-normal text-slate-500">
                          ({analytics.content?.real_questions_pct || 0}%)
                        </span>
                      </div>
                    </div>
                    
                    <div className="rounded-lg bg-slate-800/30 p-3">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Bot className="h-4 w-4" />
                        AI Answers
                      </div>
                      <div className="mt-1 text-xl font-bold text-amber-400">
                        {analytics.content?.ai_answers || 0}
                      </div>
                    </div>
                    
                    <div className="rounded-lg bg-slate-800/30 p-3">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <UserCheck className="h-4 w-4" />
                        Human Answers
                      </div>
                      <div className="mt-1 text-xl font-bold text-emerald-400">
                        {analytics.content?.human_answers || 0}
                        <span className="ml-2 text-sm font-normal text-slate-500">
                          ({analytics.content?.human_answers_pct || 0}%)
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Activity */}
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-3">
                      <div className="text-xs text-emerald-400">Today (Real Users)</div>
                      <div className="mt-1 text-2xl font-bold text-emerald-300">
                        {analytics.activity?.questions_today || 0} questions
                      </div>
                    </div>
                    <div className="rounded-lg bg-blue-500/10 border border-blue-500/20 p-3">
                      <div className="text-xs text-blue-400">This Week</div>
                      <div className="mt-1 text-2xl font-bold text-blue-300">
                        {analytics.activity?.questions_this_week || 0} questions
                      </div>
                    </div>
                    <div className="rounded-lg bg-violet-500/10 border border-violet-500/20 p-3">
                      <div className="text-xs text-violet-400">This Month</div>
                      <div className="mt-1 text-2xl font-bold text-violet-300">
                        {analytics.activity?.questions_this_month || 0} questions
                      </div>
                    </div>
                  </div>
                  
                  {/* Top Engaged Users */}
                  {analytics.users?.top_engaged?.length > 0 && (
                    <div className="rounded-lg bg-slate-800/30 p-3">
                      <div className="text-xs text-slate-500 mb-2">Top Engaged Users</div>
                      <div className="space-y-2">
                        {analytics.users.top_engaged.slice(0, 5).map((u, i) => (
                          <div key={i} className="flex items-center justify-between text-sm">
                            <span className="text-slate-300">{u.pseudonym || u.email}</span>
                            <Badge variant="outline" className="border-emerald-500/50 text-emerald-400">
                              {u.answer_count} answers
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* SYSTEM HEALTH MONITORING */}
        <Card className="mt-6 border-blue-500/20 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 backdrop-blur-sm p-6">
          <div className="flex items-start gap-4">
            <div className="rounded-full bg-blue-500/20 p-3">
              <BarChart3 className="h-6 w-6 text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-100">System Health & Monitoring</h3>
              <p className="mt-1 text-sm text-slate-400">
                Test AI systems and receive health alerts via email.
              </p>
              
              <div className="mt-4 flex flex-wrap gap-3">
                {/* Test AI Systems */}
                <Button
                  onClick={async () => {
                    setSeeding(true);
                    setSeedMessage("Testing AI systems...");
                    setSeedStatus(null);
                    try {
                      const res = await api.post("/admin/test-ai-systems");
                      setSeedStatus(res.data.overall_status === "healthy" ? "success" : "error");
                      const results = res.data.results;
                      setSeedMessage(
                        `Moderation: ${results.moderation.status} | Image Prompt: ${results.image_prompt.status} | LLM: ${results.answer_generation.status}`
                      );
                    } catch (e) {
                      setSeedStatus("error");
                      setSeedMessage(`Test failed: ${e.response?.data?.detail || e.message}`);
                    } finally {
                      setSeeding(false);
                    }
                  }}
                  disabled={seeding}
                  className="rounded-lg bg-blue-600 px-6 text-white hover:bg-blue-500 disabled:opacity-50"
                  data-testid="test-ai-btn"
                >
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Test AI Systems
                </Button>
                
                {/* Send Health Alert */}
                <Button
                  onClick={async () => {
                    setSeeding(true);
                    setSeedMessage("Sending health report...");
                    setSeedStatus(null);
                    try {
                      const res = await api.post("/admin/send-health-alert");
                      setSeedStatus("success");
                      setSeedMessage(`Health report sent to ${res.data.sent_to}`);
                    } catch (e) {
                      setSeedStatus("error");
                      setSeedMessage(`Failed to send: ${e.response?.data?.detail || e.message}`);
                    } finally {
                      setSeeding(false);
                    }
                  }}
                  disabled={seeding}
                  className="rounded-lg bg-violet-600 px-6 text-white hover:bg-violet-500 disabled:opacity-50"
                  data-testid="send-alert-btn"
                >
                  <AlertTriangle className="mr-2 h-4 w-4" />
                  Send Health Report
                </Button>
                
                {/* Get Dashboard Stats */}
                <Button
                  onClick={async () => {
                    setSeeding(true);
                    setSeedMessage("Fetching dashboard stats...");
                    setSeedStatus(null);
                    try {
                      const res = await api.get("/admin/dashboard");
                      const s = res.data.stats;
                      setSeedStatus(s.health_status === "healthy" ? "success" : "error");
                      setSeedMessage(
                        `Questions: ${s.questions.total} (${s.questions.today} today) | Answers: ${s.answers.total} (AI: ${s.answers.ai}, Human: ${s.answers.human}) | Images Missing: ${s.images.missing_images} | Duplicates: ${s.images.duplicates}`
                      );
                    } catch (e) {
                      setSeedStatus("error");
                      setSeedMessage(`Failed: ${e.response?.data?.detail || e.message}`);
                    } finally {
                      setSeeding(false);
                    }
                  }}
                  disabled={seeding}
                  className="rounded-lg bg-slate-600 px-6 text-white hover:bg-slate-500 disabled:opacity-50"
                  data-testid="dashboard-stats-btn"
                >
                  <Database className="mr-2 h-4 w-4" />
                  Full Dashboard Stats
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Reports Section */}
        <div className="mt-6">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-semibold text-slate-100">Recent Reports</h2>
            <Badge className="bg-amber-500/20 text-amber-300 border-0">{reports.length} pending</Badge>
          </div>

          <div className="space-y-2">
            {reports.length === 0 ? (
              <Card className="border-slate-700/50 bg-slate-800/30 p-6 text-center text-sm text-slate-500">
                No pending reports. Great job maintaining community standards!
              </Card>
            ) : (
              reports.map((r) => (
                <Card key={r.report_id} className="border-slate-700/50 bg-slate-800/50 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <Badge className={`${r.subject_type === "question" ? "bg-blue-500/20 text-blue-300" : "bg-emerald-500/20 text-emerald-300"} border-0`}>
                          {r.subject_type}
                        </Badge>
                        <Badge className="bg-red-500/20 text-red-300 border-0">{r.reason}</Badge>
                        {r.is_hidden && <Badge className="bg-slate-700 text-slate-400 border-0">Hidden</Badge>}
                      </div>
                      <div className="mt-2 text-sm text-slate-300 line-clamp-2">{r.content_preview}</div>
                      {r.details && (
                        <div className="mt-1 text-xs text-slate-500">Details: {r.details}</div>
                      )}
                      <div className="mt-2 text-xs text-slate-500">
                        Reported {timeAgo(r.created_at)}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {r.is_hidden ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => unhideContent(r.subject_type, r.subject_id)}
                          className="rounded-full border-slate-700 text-slate-300 hover:bg-slate-700"
                        >
                          <Eye className="mr-1 h-3 w-3" /> Unhide
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => hideContent(r.subject_type, r.subject_id)}
                          className="rounded-full border-red-500/30 text-red-400 hover:bg-red-500/20"
                        >
                          <EyeOff className="mr-1 h-3 w-3" /> Hide
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => dismissReport(r.report_id)}
                        className="rounded-full text-slate-500 hover:text-slate-300"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </div>

        {/* Quick Links */}
        <Card className="mt-6 border-slate-700/50 bg-slate-800/50 p-4">
          <h3 className="mb-3 font-semibold text-slate-100">Quick Actions</h3>
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate("/")} 
              className="rounded-full border-slate-700 text-slate-300 hover:bg-slate-700"
            >
              View Homepage <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate("/browse")} 
              className="rounded-full border-slate-700 text-slate-300 hover:bg-slate-700"
            >
              Browse Questions <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
